import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReusableModal from '../../Common/CommonModal/ReusableModal';
import CommonSelect from '../../Common/CommonSelect';
import zohoService from '../../../services/zohoService';
import { toast } from 'react-toastify';
import {
  diffAccountMappings,
  formatAccountOption,
  formatAccountOptions,
  displayLabel,
  hasMappingChanges,
  mergeSavedIntoOptions,
  sortByXeroCode,
  withXeroCodeFallback,
} from './zohoMappingUtils';
import { UniformRefBadge } from './ZohoMappingUi';

const ZohoAccountMappingEditModal = ({
  show,
  onHide,
  fields,
  accountMappings,
  onSaved,
}) => {
  const [draftMappings, setDraftMappings] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sortedFields = useMemo(() => sortByXeroCode(fields), [fields]);
  const fieldKeys = useMemo(() => sortedFields.map((f) => f.key), [sortedFields]);

  const changedMappings = useMemo(
    () => diffAccountMappings(accountMappings, draftMappings, fieldKeys),
    [accountMappings, draftMappings, fieldKeys]
  );

  const isDirty = hasMappingChanges(changedMappings);

  const loadAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await zohoService.getAccounts();
      const data = Array.isArray(response?.data) ? response.data : [];
      setAccounts(
        mergeSavedIntoOptions(
          formatAccountOptions(data),
          accountMappings,
          fields,
          formatAccountOption
        )
      );

      if (data.length === 0) {
        toast.info('No accounts with a code were found in Zoho Books.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error) {
      setAccounts([]);
      toast.error(error.message || 'Error fetching Zoho accounts', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [accountMappings, fields]);

  useEffect(() => {
    if (!show) {
      return;
    }

    setDraftMappings({ ...accountMappings });
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleFieldChange = (fieldKey, value) => {
    setDraftMappings((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleSave = async () => {
    if (!isDirty || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await zohoService.saveAccountMapping(changedMappings);
      toast.success('Account mappings saved', { position: 'top-right', autoClose: 2000 });
      if (onSaved) {
        await onSaved(response);
      }
      onHide();
    } catch (error) {
      toast.error(error.message || 'Failed to save account mappings', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) {
      return;
    }
    onHide();
  };

  const renderEditRow = (field, index) => {
    const label = displayLabel(field.label);

    return (
      <div
        key={field.key}
        className="d-flex align-items-center gap-2"
        style={{
          padding: '6px 8px',
          borderRadius: 4,
          background: index % 2 === 0 ? 'var(--bs-tertiary-bg)' : 'transparent',
        }}
      >
        {field.xero_code ? <UniformRefBadge code={field.xero_code} /> : <span style={{ width: 52, flexShrink: 0 }} />}
        <span
          className="small fw-medium"
          style={{ width: 200, flexShrink: 0, lineHeight: 1.35 }}
          title={label}
        >
          {label}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <CommonSelect
            value={withXeroCodeFallback(draftMappings[field.key], field.xero_code) || null}
            onChange={(value) => handleFieldChange(field.key, value)}
            options={accounts}
            isSubmitting={isSaving}
            errors={{}}
            placeholder="Select…"
            isClearable={false}
            className=""
          />
        </div>
      </div>
    );
  };

  return (
    <ReusableModal
      show={show}
      onHide={handleCancel}
      title="Edit account mappings"
      size="lg"
      bodyStyle={{ padding: '12px 16px', minHeight: '40vh', maxHeight: '70vh', overflowY: 'auto' }}
      closeButtonText="Cancel"
      closeButtonVariant="light"
      showCloseButton
      actions={[
        {
          text: isSaving ? 'Saving…' : 'Save',
          className: 'btn-primary',
          onClick: handleSave,
          disabled: !isDirty || isSaving || isLoading,
        },
      ]}
    >
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading accounts…</span>
          </div>
        </div>
      ) : accounts.length === 0 ? (
        <p className="text-muted small mb-0">
          No chart-of-accounts entries with a code were returned from Zoho Books.
        </p>
      ) : (
        <div className="border rounded" style={{ padding: '8px 12px' }}>
          {sortedFields.map((field, index) => renderEditRow(field, index))}
        </div>
      )}
    </ReusableModal>
  );
};

export default ZohoAccountMappingEditModal;
